import { useMemo, type ReactNode, type ComponentPropsWithoutRef } from 'react';
import { Mail, MessageSquare, PhoneCall } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/Popover';
import { brandConfig } from '../../config';
import { createMailToSupportHref } from '../../utils/owner-portal/contactUs';
import { cn } from '../../lib/utils';
import useResponsive from '../../hooks/useResponsive';

type PopoverContentProps = ComponentPropsWithoutRef<typeof PopoverContent>;

export interface ContactUsPopoverProps {
  trigger: ReactNode;
  /** When omitted, false below `sm`, true at `sm` and up. */
  displayPhoneNumber?: boolean;
  /** When omitted, false below `sm`, true at `sm` and up. */
  displayEmail?: boolean;
  /** Controlled open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Override default Popover modal behaviour (default: uncontrolled). */
  modal?: boolean;
  /** Props forwarded to PopoverContent (side, align, className, onInteractOutside, etc.). */
  contentProps?: Omit<PopoverContentProps, 'children'>;
  /** Called after a contact link is clicked. */
  onAction?: () => void;
}

const itemClass = cn(
  'flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
  'transition-colors hover:bg-accent hover:text-accent-foreground',
  'focus:bg-accent focus:text-accent-foreground'
);

export function ContactUsPopover({
  trigger,
  displayPhoneNumber,
  displayEmail,
  open,
  onOpenChange,
  modal,
  contentProps,
  onAction,
}: ContactUsPopoverProps) {
  const isMobile = useResponsive('down', 'sm');
  const showPhoneNumber = displayPhoneNumber ?? !isMobile;
  const showEmail = displayEmail ?? !isMobile;
  const textSupportNumber = brandConfig.supportText ?? brandConfig.supportPhone;

  const phoneDigits = useMemo(
    () => brandConfig.supportPhone.replace(/\D/g, ''),
    []
  );
  const textDigits = useMemo(
    () => textSupportNumber.replace(/\D/g, ''),
    [textSupportNumber]
  );

  const handleClick = () => {
    onAction?.();
  };

  const {
    className: contentClassName,
    collisionPadding = 14,
    side = 'top',
    align = 'start',
    ...restContentProps
  } = contentProps ?? {};

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={modal}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        collisionPadding={collisionPadding}
        className={cn('w-auto min-w-[10rem] max-w-[min(20rem,calc(100vw-2rem))] p-1', contentClassName)}
        {...restContentProps}
      >
        <div className="flex flex-col" role="menu" aria-label="Contact options">
          <a
            href={createMailToSupportHref('Need Assistance')}
            role="menuitem"
            className={itemClass}
            onClick={handleClick}
          >
            <Mail className="mr-2 h-4 w-4 shrink-0" />
            {showEmail ? brandConfig.supportEmail : 'Email'}
          </a>
          <a
            href={`tel:+1${phoneDigits}`}
            role="menuitem"
            className={itemClass}
            title={brandConfig.supportPhone}
            aria-label={`Call ${brandConfig.supportPhone}`}
            onClick={handleClick}
          >
            <PhoneCall className="mr-2 h-4 w-4 shrink-0" />
            {showPhoneNumber ? brandConfig.supportPhone : 'Call'}
          </a>
          <a
            href={`sms:+1${textDigits}`}
            role="menuitem"
            className={itemClass}
            title={textSupportNumber}
            aria-label={`Text ${textSupportNumber}`}
            onClick={handleClick}
          >
            <MessageSquare className="mr-2 h-4 w-4 shrink-0" />
            {showPhoneNumber ? textSupportNumber : 'Text'}
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
}
